import { getRequest, postRequest, patchRequest, patchMultipartRequest } from './api';

const getJwtToken = () => localStorage.getItem('token') || '';

const metaApi = {
  startMetaOAuth() {
    window.location.href = `${process.env.REACT_APP_API_URL}/meta-auth/login`;
  },

  startMetaOAuthWithScopes(scopeLevel = 'extended', returnTo = '') {
    const token = getJwtToken();
    const params = new URLSearchParams({
      token,
      scope_level: scopeLevel,
    });
    if (returnTo) {
      params.append('return_to', returnTo);
    }
    window.location.href = `${process.env.REACT_APP_API_URL}/meta-auth/login?${params}`;
  },

  async checkMetaPermissions() {
    return getRequest('/meta-auth/check-permissions');
  },

  async disconnectMetaAds() {
    const response = await postRequest('/meta-auth/disconnect', {});
    return response.data;
  },

  async updateMetaConnectionData({ accountId, pageId, instagramId, whatsappPhoneNumber }) {
    const payload = {
      account_id: accountId,
    };
    
    // Incluir page_id si tiene valor o es null explícito
    if (pageId !== undefined) {
      payload.page_id = pageId || null;
    }
    
    // Instagram y WhatsApp - incluir si tienen valor o son null explícito
    if (instagramId !== undefined) {
      payload.instagram_id = instagramId || null;
    }
    if (whatsappPhoneNumber !== undefined) {
      payload.whatsapp_phone_number = whatsappPhoneNumber || null;
    }
    
    const response = await postRequest('/meta-auth/update-connection-data', payload);
    return response.data;
  },

  async fetchMetaAccounts(force = false) {
    return getRequest('/meta-auth/accounts', {}, { force });
  },

  async fetchActiveMetaAccount() {
    return getRequest('/meta/active-account');
  },

  async fetchMetaUserInfo() {
    return getRequest('/meta/userinfo');
  },

  async fetchMetaCampaigns(params) {
    return getRequest('/meta/campaigns', {}, params);
  },

  async fetchUserMetaCampaigns(userId, params) {
    return getRequest(`/admin/campaigns/meta/${userId}`, {}, params);
  },

  async fetchMetaCampaignById(campaignId, { force = false } = {}) {
    return getRequest(`/meta/campaigns/${campaignId}`, {}, { force });
  },

  async fetchMetaCreativeById(creativeId, adFormat = 'MOBILE_FEED_STANDARD', options = {}) {
    const { responseFormat } = options;
    const params = { ad_format: adFormat };
    if (responseFormat) params.format = responseFormat;
    return getRequest(`/meta/creatives/${creativeId}`, {}, params);
  },

  /**
   * GET /api/meta-creative-ranking (v1, sin brand_id; sesión + account_id alineada a la cuenta Meta del usuario).
   * Ahora es asíncrono: encola un job en background y responde 202 { job_id, status }.
   * El resultado se obtiene haciendo polling con getCreativeRankingJobStatus(job_id).
   * @param {object} p
   * @param {string} p.account_id
   * @param {string} p.window yesterday|last_30_days|custom
   * @param {string} [p.date_from] window=custom
   * @param {string} [p.date_to] window=custom
   * @param {string} p.objective roas|cpa
   * @param {string[]|string} [p.conversion_types] CSV o array → se manda como CSV
   * @param {number} [p.limit]
   * @param {number} [p.offset]
   * Responde el AxiosResponse completo (status 202 + data { job_id, status }; headers, ej. X-Request-Id).
   */
  async fetchMetaCreativeRanking(p) {
    const {
      account_id,
      window: windowParam,
      date_from,
      date_to,
      objective,
      conversion_types,
      limit,
      offset,
    } = p;
    const query = { account_id, window: windowParam, objective };
    if (date_from) query.date_from = date_from;
    if (date_to) query.date_to = date_to;
    if (limit != null && limit !== '') query.limit = limit;
    if (offset != null && offset !== '') query.offset = offset;
    if (conversion_types) {
      const csv = Array.isArray(conversion_types) ? conversion_types.join(',') : String(conversion_types);
      if (csv.trim()) query.conversion_types = csv;
    }
    return getRequest('/api/meta-creative-ranking', {}, query);
  },

  /**
   * GET /api/job-status/<jobId> — estado del job de creative-ranking.
   * Devuelve la data ({ status, result, error, ... }).
   * status: pending | running | completed | failed.
   * Un 404 ({ error: 'Job not found' }) lanza un error de axios con e.response.status === 404
   * (el job expiró, TTL ~10 min) → el caller debe re-encolar un job nuevo.
   */
  async getCreativeRankingJobStatus(jobId) {
    const response = await getRequest(`/api/job-status/${jobId}`);
    return response.data;
  },

  /**
   * Quick Actions (internal playground): resolve budget context for an ad_id.
   * GET /api/meta/ads/<ad_id>/budget-context?account_id=act_...
   * Returns AxiosResponse (data + headers with X-Request-Id).
   */
  async fetchMetaAdBudgetContext(adId, { account_id } = {}) {
    return getRequest(`/api/meta/ads/${adId}/budget-context`, {}, { account_id });
  },

  /**
   * Quick Actions (internal playground): execute pause / set budget.
   * POST /api/meta/ads/actions
   * Returns AxiosResponse (data + headers with X-Request-Id).
   */
  async executeMetaAdAction(body) {
    return postRequest('/api/meta/ads/actions', body);
  },

  async fetchMetaAnalyticsData(start_date, end_date) {
    return getRequest('/api/meta-ads', {}, { start_date, end_date });
  },

  async updateMetaAdsCampaign(campaignId, { payload, isMultipart }) {
    if (isMultipart) {
      const response = await patchMultipartRequest(`/meta/campaigns/${campaignId}`, payload)
      return response.data
    } else {
      const response = await patchRequest(`/meta/campaigns/${campaignId}`, payload)
      return response.data
    }
  },  

  async getCatalog(catalogId) {
    return getRequest(`/meta/catalogs/${catalogId}`);
  },
};

export default metaApi;